import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Settings, Loader2, HardDrive, KeyRound, MessageSquare, Cpu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getDriveSettingsBundle,
  updateAssistantSettings,
  updateDriveConfig,
  updateSlackSettings,
} from '../features/admin/driveSettingsApi';
import {
  AssistantSettingsPanel,
  SlackSettingsPanel,
  StorageSettingsPanel,
} from '../features/admin/DriveSettingsPanels';
import FileBackfillPanel from '../features/admin/FileBackfillPanel';
import ApiKeysPanel from '../features/admin/ApiKeysPanel';
import AiModelsPanel from '../features/admin/AiModelsPanel';

export default function DriveSettings() {
  const { can, loading: authLoading } = useAuth();
  const canManageSettings = can('manage_settings');
  const [activeTab, setActiveTab] = useState('drive');
  const [config, setConfig] = useState({ sharedDriveId: '', rootFolderId: '' });
  const [slackConfig, setSlackConfig] = useState({
    legalNotificationsChannel: '',
    legalNotificationsEmail: '',
  });
  const [slackFallbacks, setSlackFallbacks] = useState({
    legalNotificationsChannel: '',
  });
  const [assistantConfig, setAssistantConfig] = useState({
    searchResultLimit: 10,
    clarificationChoiceLimit: 10,
    expiringContractsLimit: 10,
    nextStepsLimit: 4,
  });
  const [assistantBounds, setAssistantBounds] = useState({
    resultLimits: { min: 1, max: 25 },
    nextStepsLimit: { min: 1, max: 10 },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSlack, setSavingSlack] = useState(false);
  const [savingAssistant, setSavingAssistant] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [slackFeedback, setSlackFeedback] = useState({ type: '', message: '' });
  const [assistantFeedback, setAssistantFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!canManageSettings) return;
    getDriveSettingsBundle()
      .then(([data, slackData, assistantData]) => {
        setConfig({
          sharedDriveId: data.sharedDriveId || '',
          rootFolderId: data.rootFolderId || '',
        });
        setSlackConfig({
          legalNotificationsChannel:
            slackData.configured?.legalNotificationsChannel || slackData.legalNotificationsChannel || '',
          legalNotificationsEmail: slackData.legalNotificationsEmail || '',
        });
        setSlackFallbacks({
          legalNotificationsChannel: slackData.envFallbacks?.legalNotificationsChannel || '',
        });
        setAssistantConfig({
          searchResultLimit: assistantData.searchResultLimit || 10,
          clarificationChoiceLimit: assistantData.clarificationChoiceLimit || 10,
          expiringContractsLimit: assistantData.expiringContractsLimit || 10,
          nextStepsLimit: assistantData.nextStepsLimit || 4,
        });
        setAssistantBounds({
          resultLimits: assistantData.bounds?.resultLimits || { min: 1, max: 25 },
          nextStepsLimit: assistantData.bounds?.nextStepsLimit || { min: 1, max: 10 },
        });
        setLoading(false);
      })
      .catch((err) => {
        setFeedback({ type: 'error', message: `Failed to load: ${err.message}` });
        setLoading(false);
      });
  }, [canManageSettings]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback({ type: '', message: '' });
    try {
      await updateDriveConfig(config);
      setFeedback({ type: 'success', message: 'Configuration saved.' });
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSlack = async () => {
    setSavingSlack(true);
    setSlackFeedback({ type: '', message: '' });
    try {
      const data = await updateSlackSettings(slackConfig);
      setSlackConfig({
        legalNotificationsChannel:
          data.configured?.legalNotificationsChannel || data.legalNotificationsChannel || '',
        legalNotificationsEmail: data.legalNotificationsEmail || '',
      });
      setSlackFallbacks({
        legalNotificationsChannel: data.envFallbacks?.legalNotificationsChannel || '',
      });
      setSlackFeedback({ type: 'success', message: 'Slack settings saved.' });
    } catch (err) {
      setSlackFeedback({ type: 'error', message: err.message });
    } finally {
      setSavingSlack(false);
    }
  };

  const handleSaveAssistant = async () => {
    setSavingAssistant(true);
    setAssistantFeedback({ type: '', message: '' });
    try {
      const data = await updateAssistantSettings(assistantConfig);
      setAssistantConfig({
        searchResultLimit: data.searchResultLimit || 10,
        clarificationChoiceLimit: data.clarificationChoiceLimit || 10,
        expiringContractsLimit: data.expiringContractsLimit || 10,
        nextStepsLimit: data.nextStepsLimit || 4,
      });
      setAssistantBounds({
        resultLimits: data.bounds?.resultLimits || assistantBounds.resultLimits,
        nextStepsLimit: data.bounds?.nextStepsLimit || assistantBounds.nextStepsLimit,
      });
      setAssistantFeedback({ type: 'success', message: 'Assistant settings saved.' });
    } catch (err) {
      setAssistantFeedback({ type: 'error', message: err.message });
    } finally {
      setSavingAssistant(false);
    }
  };

  const settingsTabs = [
    { key: 'drive', label: 'Google Drive', icon: HardDrive },
    { key: 'notifications', label: 'Notifications', icon: MessageSquare },
    { key: 'assistant', label: 'Assistant', icon: Settings },
    { key: 'models', label: 'AI models', icon: Cpu },
    { key: 'api', label: 'API keys', icon: KeyRound },
  ];

  if (authLoading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  if (!canManageSettings) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">Admin settings</h1>
          <div className="page-subtitle">
            Configure integrations and assistant behavior, inspect AI models, and manage service API keys.
          </div>
        </div>
      </div>

      <div className="tabs" role="tablist" aria-label="Admin settings sections">
        {settingsTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.count ? <span className="ct">{tab.count}</span> : null}
            </button>
          );
        })}
      </div>

      {activeTab === 'drive' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <StorageSettingsPanel
            config={config}
            feedback={feedback}
            handleSave={handleSave}
            saving={saving}
            setConfig={setConfig}
            setFeedback={setFeedback}
          />
          <FileBackfillPanel />
        </div>
      )}

      {activeTab === 'notifications' && (
        <SlackSettingsPanel
          handleSaveSlack={handleSaveSlack}
          savingSlack={savingSlack}
          setSlackConfig={setSlackConfig}
          setSlackFeedback={setSlackFeedback}
          slackConfig={slackConfig}
          slackFallbacks={slackFallbacks}
          slackFeedback={slackFeedback}
        />
      )}

      {activeTab === 'assistant' && (
        <AssistantSettingsPanel
          assistantBounds={assistantBounds}
          assistantConfig={assistantConfig}
          assistantFeedback={assistantFeedback}
          handleSaveAssistant={handleSaveAssistant}
          savingAssistant={savingAssistant}
          setAssistantConfig={setAssistantConfig}
          setAssistantFeedback={setAssistantFeedback}
        />
      )}

      {activeTab === 'api' && <ApiKeysPanel />}
      {activeTab === 'models' && <AiModelsPanel />}

    </div>
  );
}
